import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { ContentOverrideKind, } from '@wit-support/admin-contracts';
import { Badge, ConfirmDialog, Dialog, DialogContent, DialogDescription, DialogTitle, toast, Tooltip, } from '@wit-support/ui';
import { CONTENT_OVERRIDE_KIND_LABEL_RU, addDaysYmd, formatCalendarDay, formatInZone, startOfDayUtc, } from '@wit-support/ui-domain';
import { Actions, CancelButton, SubmitButton, When, ZodField, ZodForm, } from '@wit-support/ui-forms';
import { createOverride, deleteOverride } from '../api.js';
import { CONTENT_KEY, describeContentError, submitting } from '../shared.js';
import { CONTENT_BUSINESS_TZ } from '../period.js';
const DAY_MS = 24 * 60 * 60 * 1000;
const YMD = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Форма переопределения. Срок задаётся либо в ДНЯХ («Отдых на N дней»),
 * либо календарной датой «до какого дня включительно» — у любого вида;
 * на провод уходит мгновение `until`. Что срок в будущем, проверяет сервер
 * по своим часам (`400 until_in_past`).
 *
 * Дни — по зоне расписания движка (`CONTENT_BUSINESS_TZ`), как у фильтров
 * периода: «до 20 октября» значит до конца 20 октября по Лос-Анджелесу у
 * любого админа, и бейдж покажет тот же день.
 *
 * Служебная «проба» (`probe`) ставит сам движок после залёта; создать её
 * здесь нельзя, карточка клипа показывает её только для чтения (`probes`).
 */
export const OverrideFormSchema = z
    .object({
    kind: ContentOverrideKind.meta({
        label: 'Что сделать с клипом',
        options: ContentOverrideKind.options.map((k) => ({
            value: k,
            label: CONTENT_OVERRIDE_KIND_LABEL_RU[k],
        })),
    }),
    restDays: z
        .number({ message: 'Укажите число дней' })
        .int('Целое число дней')
        .min(1, 'Не меньше 1 дня')
        .max(90, 'Не больше 90 дней')
        .meta({ label: 'Отдых, дней', placeholder: '7' })
        .optional(),
    untilDate: z
        .string()
        .max(10)
        .meta({
        label: 'До какого дня (включительно)',
        description: 'Необязательно: без даты «Ставить первым» и «Не ставить первым» действуют, пока их не снимут. День — по времени расписания (Лос-Анджелес).',
    })
        .optional(),
    note: z
        .string()
        .trim()
        .max(500, 'Не длиннее 500 символов')
        .meta({ label: 'Заметка (необязательно)', placeholder: 'Почему' })
        .optional(),
})
    .superRefine((v, ctx) => {
    const date = v.untilDate?.trim() ?? '';
    if (date !== '' && !YMD.test(date)) {
        ctx.addIssue({ code: 'custom', path: ['untilDate'], message: 'Дата в формате ГГГГ-ММ-ДД' });
    }
    if (v.kind === 'rest' && v.restDays === undefined && date === '') {
        ctx.addIssue({ code: 'custom', path: ['restDays'], message: 'Укажите, на сколько дней' });
    }
});
/**
 * Значения формы → тело `POST /overrides`. Дата «до» включительно — на
 * провод уходит исключающая граница: начало СЛЕДУЮЩЕГО дня в зоне расписания
 * движка; она важнее числа дней. Обратно бейдж показывает
 * {@link overrideLastDay} — тот же выбранный день.
 */
export function toOverrideRequest(account, clip, v, now = Date.now(), tz = CONTENT_BUSINESS_TZ) {
    const note = v.note?.trim();
    const date = v.untilDate?.trim() ?? '';
    const until = date !== '' && YMD.test(date)
        ? startOfDayUtc(addDaysYmd(date, 1), tz)
        : v.kind === 'rest' && v.restDays !== undefined
            ? new Date(now + v.restDays * DAY_MS).toISOString()
            : undefined;
    return {
        account,
        clip,
        kind: v.kind,
        ...(until !== undefined ? { until } : {}),
        ...(note ? { note } : {}),
    };
}
/**
 * Чего коснётся снятие. `account === null` — глобальное переопределение движка:
 * снять его — вернуть клип ВО ВСЕХ аккаунтах, и оператор обязан это прочитать.
 */
export function overrideScopeText(o) {
    // Карточка движка аккаунт переопределения не отдаёт: не утверждать «везде».
    if (o.accountKnown === false) {
        return 'снимется (аккаунт движок не сообщил: если ставилось на все аккаунты — снимется везде)';
    }
    return o.account === null
        ? 'действует для всех аккаунтов — снимется везде'
        : `снимется для аккаунта ${o.account}`;
}
/**
 * Последний день действия переопределения (`YYYY-MM-DD`, зона расписания):
 * `until` — исключающая граница, поэтому день берётся у мгновения на 1 мс
 * раньше. Дата из формы «до 20.10» (until = 21.10 00:00 LA) → «20.10».
 */
export function overrideLastDay(until, tz = CONTENT_BUSINESS_TZ) {
    return formatInZone(new Date(Date.parse(until) - 1), 'yyyy-MM-dd', tz);
}
/** «20 окт. 2026» — последний день включительно, как его выбрали в форме. */
export function OverrideUntil({ until }) {
    const day = overrideLastDay(until);
    return (_jsx("span", { title: "\u0432\u043A\u043B\u044E\u0447\u0438\u0442\u0435\u043B\u044C\u043D\u043E, \u043F\u043E \u0432\u0440\u0435\u043C\u0435\u043D\u0438 \u0440\u0430\u0441\u043F\u0438\u0441\u0430\u043D\u0438\u044F (\u041B\u043E\u0441-\u0410\u043D\u0434\u0436\u0435\u043B\u0435\u0441)", children: formatCalendarDay(day, 'd MMM yyyy') }));
}
const OVERRIDE_VARIANT = {
    exclude: 'danger',
    pin: 'warning',
    rest: 'neutral',
};
export function OverrideBadge({ override: o }) {
    const badge = (_jsxs(Badge, { variant: OVERRIDE_VARIANT[o.kind], "data-kind": o.kind, ...(o.note ? { tabIndex: 0 } : {}), children: [CONTENT_OVERRIDE_KIND_LABEL_RU[o.kind], o.until ? (_jsxs(_Fragment, { children: [' ', "\u0434\u043E ", _jsx(OverrideUntil, { until: o.until })] })) : null] }));
    if (!o.note)
        return badge;
    // Заметка — во всплывающей подсказке кита, а не в `title`: `title` виден
    // только мышью, а бейдж с подсказкой фокусируется с клавиатуры.
    return (_jsx(Tooltip.Provider, { children: _jsxs(Tooltip.Root, { children: [_jsx(Tooltip.Trigger, { asChild: true, children: badge }), _jsx(Tooltip.Content, { className: "max-w-xs whitespace-normal", children: o.note })] }) }));
}
export function OverrideBadges({ overrides }) {
    if (overrides.length === 0)
        return _jsx("span", { className: "text-[var(--color-text-muted)]", children: "\u2014" });
    return (_jsx("div", { className: "flex flex-wrap gap-1", children: overrides.map((o) => (_jsx(OverrideBadge, { override: o }, o.id))) }));
}
/**
 * Переопределения клипа: диалог «что сделать» и подтверждение снятия.
 * Мутации — существующие `POST /overrides` / `DELETE /overrides/:id`
 * (`content.manage` + аудит на admin-api). После успеха инвалидируется весь
 * раздел: список, карточка и обзор читают одно и то же.
 */
export function useClipOverrideActions(account) {
    const qc = useQueryClient();
    const [creating, setCreating] = useState(null);
    const [removing, setRemoving] = useState(null);
    const invalidate = () => qc.invalidateQueries({ queryKey: CONTENT_KEY });
    const createMut = useMutation({
        mutationFn: (body) => createOverride(body),
        onSuccess: (r) => {
            toast.success(`«${CONTENT_OVERRIDE_KIND_LABEL_RU[r.kind]}» сохранено`);
            setCreating(null);
            void invalidate();
        },
        onError: (err) => toast.error(describeContentError(err, 'write')),
    });
    const deleteMut = useMutation({
        mutationFn: (id) => deleteOverride(id),
        onSuccess: () => {
            toast.success('Переопределение снято');
            void invalidate();
        },
        onError: (err) => toast.error(describeContentError(err, 'write')),
    });
    const dialogs = (_jsxs(_Fragment, { children: [creating ? (_jsx(Dialog, { open: true, onOpenChange: (v) => (!v ? setCreating(null) : null), children: _jsxs(DialogContent, { children: [_jsxs(DialogTitle, { children: ["\u041A\u043B\u0438\u043F \u00AB", creating.clip.name, "\u00BB"] }), _jsxs(DialogDescription, { children: ["\u0414\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442 \u0434\u043B\u044F \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430 ", account, " \u0441\u043E \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0433\u043E \u0440\u043E\u043B\u0438\u043A\u0430. \u0421\u043D\u044F\u0442\u044C \u043C\u043E\u0436\u043D\u043E \u0432 \u043B\u044E\u0431\u043E\u0439 \u043C\u043E\u043C\u0435\u043D\u0442."] }), _jsxs(ZodForm, { schema: OverrideFormSchema, defaultValues: { kind: creating.kind, note: '', untilDate: '' }, onSubmit: (values) => submitting(createMut.mutateAsync(toOverrideRequest(account, creating.clip.id, values, Date.now()))), children: [_jsx(ZodField, { name: "kind" }), _jsx(When, { field: "kind", eq: "rest", children: _jsx(ZodField, { name: "restDays", hint: "\u0418\u043B\u0438 \u0443\u043A\u0430\u0436\u0438\u0442\u0435 \u0434\u0430\u0442\u0443 \u043D\u0438\u0436\u0435." }) }), _jsx(ZodField, { name: "untilDate", type: "date" }), _jsx(ZodField, { name: "note" }), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: () => setCreating(null) }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u043C\u2026", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] })] }) })) : null, _jsx(ConfirmDialog, { open: removing !== null, onOpenChange: (v) => (!v ? setRemoving(null) : null), title: "\u0421\u043D\u044F\u0442\u044C \u043F\u0435\u0440\u0435\u043E\u043F\u0440\u0435\u0434\u0435\u043B\u0435\u043D\u0438\u0435?", description: removing
                    ? `«${CONTENT_OVERRIDE_KIND_LABEL_RU[removing.override.kind]}» для клипа «${removing.clip.name}» ${overrideScopeText(removing.override)}. После снятия движок снова будет выбирать этот клип сам.`
                    : '', cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u0421\u043D\u044F\u0442\u044C", onConfirm: () => {
                    if (removing)
                        deleteMut.mutate(removing.override.id);
                    setRemoving(null);
                } })] }));
    return {
        openCreate: (clip, kind) => setCreating({ clip, kind }),
        openRemove: (clip, override) => setRemoving({ clip, override }),
        dialogs,
    };
}
//# sourceMappingURL=clip-overrides.js.map